import React, { useState, useEffect } from "react";
import { Text, toast } from "@jsq/ds/core";
import { toISOFormat } from "~/utils/datetime";
import { parseDateOnlyString, formatDateOnlyString } from "../../../utils/datetime";
import { validateFieldValue, EXTENSION_FIELD_TYPE_IDS } from "../../utils/extensionFieldUtils";
import { FieldTypeComponents } from "./FieldTypeComponents";
import * as styles from "./styles_vanilla.css";
import type { ExtensionFieldObjectType, ExtensionFieldValueObjectType } from "../../graphql";

interface ExtensionFieldValueEditorProps {
  /** Extension field metadata */
  field: ExtensionFieldObjectType;
  /** Current field value */
  currentValue: ExtensionFieldValueObjectType | null;
  /** Callback for save operations */
  onSave: (value: string) => Promise<void>;
}

/**
 * Extension Field Value Editor Component
 *
 * Provides inline editing for extension field values with type-appropriate form controls.
 * Used in bulk editing mode (alwaysVisible=true) within EntityDetailModal.
 */
const formatFieldValueForInput = (
  value: ExtensionFieldValueObjectType,
  fieldType: number,
): string => {
  if (!value) return "";

  switch (fieldType) {
    case EXTENSION_FIELD_TYPE_IDS.INTEGER:
      return value.intValue?.toString() || "";
    case EXTENSION_FIELD_TYPE_IDS.FLOAT:
      return value.floatValue?.toString() || "";
    case EXTENSION_FIELD_TYPE_IDS.TEXT:
      return value.stringValue || "";
    case EXTENSION_FIELD_TYPE_IDS.ENUMERATION:
      return value.enumValue?.enumValueCode || "";
    case EXTENSION_FIELD_TYPE_IDS.DATE: {
      if (!value.dateValue) return "";
      try {
        const dateString = value.dateValue;
        // Use timezone-safe parser to avoid conversion issues
        const parsedDate = parseDateOnlyString(dateString);
        if (parsedDate) {
          return formatDateOnlyString(parsedDate);
        }
        // Fallback to original logic if parsing fails
        return toISOFormat(dateString);
      } catch {
        toast.error(
          "Failed to parse date value. Please refresh if the issue persists or contact engineering.",
        );
        return "";
      }
    }
    case EXTENSION_FIELD_TYPE_IDS.PERCENTILE:
      if (value.floatValue !== undefined && value.floatValue !== null) {
        return value.floatValue.toString();
      }
      return "";
    case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD:
      return value.floatValue?.toString() || "";
    default:
      return value.stringValue || "";
  }
};

export const ExtensionFieldValueEditor: React.FC<ExtensionFieldValueEditorProps> = ({
  field,
  currentValue,
  onSave,
}) => {
  const [formValue, setFormValue] = useState<string>(() => {
    if (!currentValue) return "";
    return formatFieldValueForInput(currentValue, field.extensionFieldTypeId);
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  // Update form value when external value changes
  useEffect(() => {
    const newFormValue = currentValue
      ? formatFieldValueForInput(currentValue, field.extensionFieldTypeId)
      : "";

    setFormValue(newFormValue);
    setValidationError(null);
  }, [currentValue, field.extensionFieldTypeId]);

  const handleValueChange = (newValue: string) => {
    setFormValue(newValue);

    const error = validateFieldValue(newValue, field.extensionFieldTypeId);
    setValidationError(error);

    // Update parent component's form state
    void onSave(newValue);
  };

  const FieldComponent =
    FieldTypeComponents[field.extensionFieldTypeId as keyof typeof FieldTypeComponents] ||
    FieldTypeComponents.default;

  return (
    <div className={styles.fieldEditorAlwaysVisible} data-testid="field-editor" role="group">
      <div className={styles.editorInput}>
        <FieldComponent
          field={field}
          value={formValue}
          onChange={handleValueChange}
          error={validationError}
          disabled={false}
        />
      </div>

      {validationError && (
        <div className={styles.validationError} data-testid="validation-error">
          <Text variant="regular-sm" className={styles.errorText}>
            {validationError}
          </Text>
        </div>
      )}
    </div>
  );
};

export default ExtensionFieldValueEditor;
